import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Leaf, Store, Sunrise } from 'lucide-react';

import AwardBadges from '../../components/public/AwardBadges';
import InquiryForm from '../../components/public/InquiryForm';
import Faq from '../../components/public/Faq';
import PublicLayout from '../../components/public/PublicLayout';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import {
  business,
  cafePartners,
  serviceArea,
  wholesaleFaq,
  wholesaleProof,
  wholesaleSteps,
} from '../../content/siteContent.mjs';

const PROOF_ICONS = { award: Award, sunrise: Sunrise, store: Store, leaf: Leaf };

const Wholesale = () => (
  <PublicLayout testId="public-wholesale">
    <section className="pt-16 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-4xl mx-auto text-center">
        <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-amber-600">
          Wholesale supply
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
          Wholesale donut supply across Westchester and Connecticut
        </h1>
        <p className="text-lg text-gray-600 mb-8 leading-relaxed">
          We bake to order overnight in {business.baseTown} and deliver in the morning, so your counter opens
          with donuts made hours earlier. Standing orders, seasonal specials, vegan versions and a portal that
          remembers your week — orders close {business.leadTime} before delivery.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button asChild size="lg" className="bg-gray-900 text-white hover:bg-gray-800 text-lg px-8 py-6">
            <Link to="/wholesale#inquiry" data-testid="wholesale-hero-cta">
              Send an inquiry
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="border-gray-900 text-gray-900 hover:bg-gray-50 text-lg px-8 py-6"
          >
            <Link to="/menu">See the menu</Link>
          </Button>
        </div>
        <div className="mt-10 flex justify-center">
          <AwardBadges compact />
        </div>
      </div>
    </section>

    <section className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
          What you get as a partner
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {wholesaleProof.map((proof) => {
            const Icon = PROOF_ICONS[proof.icon];
            return (
              <Card key={proof.title} className="p-8 hover:shadow-xl transition-shadow duration-300">
                <div className="mb-6 inline-flex rounded-full bg-amber-100 p-4">
                  <Icon size={26} className="text-amber-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{proof.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{proof.body}</p>
              </Card>
            );
          })}
        </div>
      </div>
    </section>

    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-amber-50/60">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
          From inquiry to delivery
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {wholesaleSteps.map((step, i) => (
            <Card key={step.title} className="p-8 bg-white hover:shadow-xl transition-shadow duration-300">
              <span className="mb-5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-gray-900 text-base font-bold text-white">
                {i + 1}
              </span>
              <h3 className="text-lg font-bold text-gray-900 mb-3">{step.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{step.body}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Towns we deliver to</h2>
        <p className="text-lg text-gray-600 mb-10">
          Morning delivery across {business.region} and {business.nearby}. Just outside the list? Ask — routes
          change as we add partners.
        </p>
        <div className="flex flex-wrap justify-center gap-3" data-testid="wholesale-service-area">
          {serviceArea.map((town) => (
            <span
              key={town}
              className="rounded-full border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-medium text-gray-700"
            >
              {town}
            </span>
          ))}
        </div>
        <p className="mt-8 text-base text-gray-600">
          Already stocked by {cafePartners.length} local businesses —{' '}
          <Link to="/where-to-find-us" className="font-semibold text-amber-700 hover:text-amber-800">
            see who
          </Link>
          .
        </p>
      </div>
    </section>

    <section id="inquiry" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 scroll-mt-24">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Start a wholesale inquiry</h2>
          <p className="text-lg text-gray-600">
            Tell us your town and rough daily volume. We reply within one business day with what we can supply
            and when — no obligation, no newsletter.
          </p>
        </div>
        <InquiryForm variant="wholesale" testId="wholesale-inquiry-form" />
      </div>
    </section>

    <section className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-10 text-center">
          Wholesale questions, answered
        </h2>
        <Faq items={wholesaleFaq} testIdPrefix="wholesale-faq" />
        <p className="mt-8 text-center text-base text-gray-600">
          Still unsure?{' '}
          <Link to="/contact" className="font-semibold text-amber-700 hover:text-amber-800">
            Send us a message
          </Link>{' '}
          or email{' '}
          <a href={`mailto:${business.email}`} className="font-semibold text-amber-700 hover:text-amber-800">
            {business.email}
          </a>
          .
        </p>
      </div>
    </section>
  </PublicLayout>
);

export default Wholesale;
